import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../components/Toast';
import LoadingScreen from '../components/LoadingScreen';
import ProgressiveHeroVideo from '../components/ProgressiveHeroVideo';

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { signInWithGoogle, isAuthenticated, loading } = useAuth();
  const redirectParam = searchParams.get('redirect');
  const isGiftFlow = searchParams.get('gift') === '1' || (redirectParam || '').startsWith('/redeem');

  useEffect(() => {
    if (redirectParam) {
      sessionStorage.setItem('auth_redirect', redirectParam);
    }
  }, [redirectParam]);

  useEffect(() => {
    if (loading || !isAuthenticated) return;

    const storedRedirect = sessionStorage.getItem('auth_redirect');
    if (storedRedirect) {
      sessionStorage.removeItem('auth_redirect');
      navigate(storedRedirect, { replace: true });
      return;
    }

    navigate('/storyverse', { replace: true });
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    const panel = document.querySelector('.auth-panel');
    if (panel) {
      panel.style.opacity = '0';
      panel.style.transform = 'translateY(30px) scale(0.95)';

      setTimeout(() => {
        panel.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        panel.style.opacity = '1';
        panel.style.transform = 'translateY(0) scale(1)';
      }, 100);
    }

    const video = document.querySelector('.auth-hero-video');
    if (video) {
      const playVideo = () => {
        video.play().catch(e => {
          logger.log('Video autoplay prevented:', e);
    });
  };

      playVideo();

      document.addEventListener('click', playVideo, { once: true });
      document.addEventListener('touchstart', playVideo, { once: true });
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        window.history.back();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      logger.log("Starting Google OAuth...");
      setIsLoading(true);
      if (redirectParam) {
        sessionStorage.setItem('auth_redirect', redirectParam);
      }

      const timeoutId = setTimeout(() => {
        setIsLoading(false);
        showToast('Sign in is taking too long. Please try again.', 'error');
      }, 15000);

      const result = await signInWithGoogle();
      clearTimeout(timeoutId);

      if (result && result.success) {
        logger.log("Google OAuth initiated successfully");
      } else {
        setIsLoading(false);
        showToast(result?.error || 'Sign in failed. Please try again.', 'error');
      }

    } catch (error) {
      logger.error("Google sign in error:", error);
      setIsLoading(false);
      showToast(error.message || 'Failed to sign in with Google', 'error');
    }
  };

  const handleBackClick = () => {
    window.location.href = "/";
  };

  if (isLoading) {
    return <LoadingScreen message="Signing you in..." fullScreen />;
  }

  return (
    <>
      <AuthPageStyles />

      {/* Back Button */}
      <button onClick={handleBackClick} className="back-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 19-7-7 7-7"/>
          <path d="M19 12H5"/>
        </svg>
        Back to Home
      </button>

      {/* Hero Video Background — same progressive 720p→4K video as the landing page */}
      <div className="auth-hero-container">
        <ProgressiveHeroVideo />
        <div className="auth-hero-overlay"></div>
      </div>

      {/* Auth Container */}
      <div className="auth-container">
        <div className={`auth-panel ${isLoading ? 'loading' : ''}`}>
          {/* Panda Logo - no background */}
          <div className="auth-logo">
            <img src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
          </div>

          {/* Success Checkmark (Hidden by default) */}
          <div className={`success-checkmark ${showSuccess ? 'show' : ''}`}></div>

          {/* Auth Form */}
          <div style={{ display: showSuccess ? 'none' : 'block' }}>
            <span className="auth-eyebrow">Let's make magic</span>
            <h1 className="auth-title">{isGiftFlow ? 'Your gift is waiting! 🎁' : 'Welcome to Pixley!'}</h1>
            <p className="auth-subtitle">{isGiftFlow ? 'Create a free account (about a minute) and your gift unlocks automatically' : 'Sign in to create magical cartoons'}</p>

            {/* Google Sign-In Button */}
            <button
              className="google-signin-btn"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <span className="google-icon-chip">
              <svg className="google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              </span>
              Continue with Google
            </button>

          </div>
        </div>
      </div>
    </>
  );
};

const AuthPageStyles = () => (
  <style jsx>{`
    body {
      font-family: 'Inter', 'Fredoka', sans-serif;
      overflow-x: hidden;
    }

    /* Hero Video Background */
    .auth-hero-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 1;
      background: #FFF;
    }

    /* Same legibility gradient as the landing hero */
    .auth-hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        180deg,
        rgba(11, 26, 51, 0.28) 0%,
        rgba(0, 101, 248, 0.14) 45%,
        rgba(11, 26, 51, 0.5) 100%
      );
    }

    /* Auth Container */
    .auth-container {
      position: relative;
      z-index: 10;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem;
    }

    /* Panel — frosted glass so the hero video stays visible behind it */
    .auth-panel {
      background: rgba(255, 255, 255, 0.14);
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
      border: 1px solid rgba(255, 255, 255, 0.28);
      border-radius: 28px;
      padding: 40px 36px 32px;
      box-shadow: 0px 24px 64px rgba(11,26,51,0.30);
      max-width: 440px;
      width: 100%;
      text-align: center;
      font-family: 'Inter', 'Fredoka', sans-serif;
    }

    /* Logo — no background, just the panda */
    .auth-logo {
      margin: 0 auto 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .auth-logo img {
      width: 80px;
      height: 80px;
      object-fit: contain;
    }

    /* Eyebrow — blue letterspaced caps, like the funnel/landing sections */
    .auth-eyebrow {
      display: block;
      font-family: 'Fredoka', sans-serif;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #9FD0FF;
      text-shadow: 0 1px 8px rgba(11,26,51,0.35);
      margin: 0 0 6px;
    }

    /* Title */
    .auth-title {
      font-family: 'Fredoka', sans-serif;
      font-size: 30px;
      font-weight: 700;
      color: #FFFFFF;
      text-shadow: 0 2px 12px rgba(11,26,51,0.4);
      margin: 0 0 8px;
      line-height: 1.2;
    }

    /* Subtitle */
    .auth-subtitle {
      font-size: 15px;
      color: rgba(255,255,255,0.85);
      text-shadow: 0 1px 8px rgba(11,26,51,0.35);
      margin: 0 0 28px;
      line-height: 1.5;
    }

    /* Google Sign-In Button — white 3D pill with brand-blue text */
    .google-signin-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 15px 24px;
      background: #FFFFFF;
      border: 2px solid rgba(0,101,248,0.16);
      border-radius: 999px;
      font-size: 18px;
      font-weight: 700;
      font-family: 'Fredoka', sans-serif;
      color: #0065F8;
      letter-spacing: 0.01em;
      cursor: pointer;
      box-shadow:
        0px 4px 0px rgba(11,26,51,0.12),
        0px 12px 26px rgba(11,26,51,0.22);
      transition: all 150ms cubic-bezier(.2,.8,.2,1);
      margin-bottom: 16px;
    }

    .google-signin-btn:hover {
      transform: translateY(-2px);
      border-color: rgba(0,101,248,0.30);
      box-shadow:
        0px 6px 0px rgba(11,26,51,0.14),
        0px 14px 30px rgba(11,26,51,0.24);
    }

    .google-signin-btn:active {
      transform: translateY(1px);
      box-shadow:
        0px 2px 0px rgba(11,26,51,0.12),
        0px 8px 18px rgba(11,26,51,0.18);
    }

    .google-signin-btn:disabled {
      opacity: 0.6;
      pointer-events: none;
    }

    .google-icon-chip {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .google-icon {
      width: 22px;
      height: 22px;
    }

    /* Back Button */
    .back-btn {
      position: absolute;
      top: 24px;
      left: 24px;
      background: #FFFFFF;
      border: 2px solid rgba(0, 101, 248, 0.18);
      border-radius: 999px;
      padding: 10px 18px;
      color: #0065F8;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 700;
      font-family: 'Fredoka', sans-serif;
      box-shadow: 0px 3px 0px rgba(11,26,51,0.08);
      transition: all 150ms ease;
      z-index: 20;
      cursor: pointer;
    }

    .back-btn:hover {
      transform: translateX(-2px) translateY(-1px);
      box-shadow: 0px 4px 0px rgba(11,26,51,0.10);
    }

    /* Success Animation */
    .success-checkmark {
      display: none;
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3EDC8F, #20A96A);
      margin: 0 auto 1rem;
      position: relative;
    }

    .success-checkmark::after {
      content: '\\2713';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 2rem;
      font-weight: bold;
    }

    .success-checkmark.show {
      display: block;
      animation: successPop 0.6s ease-out;
    }

    @keyframes successPop {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }

    /* Mobile Responsiveness */
    @media (max-width: 768px) {
      .auth-container { padding: 1rem; }
      .auth-panel { padding: 32px 24px 28px; border-radius: 24px; }
      .auth-title { font-size: 24px; }
      .back-btn { top: 16px; left: 16px; padding: 8px 14px; font-size: 13px; }
      .auth-logo img { width: 64px; height: 64px; }
    }

    @media (max-width: 480px) {
      .auth-panel { padding: 28px 20px 24px; margin: 0 8px; }
      .auth-title { font-size: 22px; }
      .auth-subtitle { font-size: 14px; margin-bottom: 24px; }
    }

    /* Loading state */
    .loading .auth-panel {
      opacity: 0.7;
      pointer-events: none;
    }
  `}</style>
);

export default AuthPage;
